import type { AnalyticsRepository } from '@/repositories/AnalyticsRepository'
import type { UserRepository } from '@/repositories/UserRepository'
import type { OrganizationRepository } from '@/repositories/OrganizationRepository'
import type { EpisodeRepository } from '@/repositories/EpisodeRepository'
import type { ProgressRepository } from '@/repositories/ProgressRepository'
import type { ResultsRepository } from '@/repositories/ResultsRepository'
import type { XPRepository } from '@/repositories/XPRepository'
import type { BadgeRepository } from '@/repositories/BadgeRepository'
import type { CertificateRepository } from '@/repositories/CertificateRepository'
import type {
  LearnerSummaryRow,
  LeaderboardEntry,
  OrgMetrics,
  LearnerStatus,
  BadgeDistributionEntry,
  EpisodePerformanceEntry,
} from '@/domain/analytics'
import type { AppUser } from '@/domain/user'
import type { UserRole } from '@/domain/roles'

// Roles that count as "learners" for org-level reporting. Staff roles
// (teacher, org_admin, super_admin) are excluded from learner headcounts,
// completion rates, and the leaderboard — they manage the roster, they
// aren't part of it (Document 5 §2 role semantics).
const LEARNER_ROLES: UserRole[] = ['school_student', 'corporate_employee']

// Zero-cost implementation of AnalyticsRepository (Document 11 §4). Computes
// every rollup on the client from the same local Progress/Results/XP/Badge/
// Certificate repositories P1 already writes to — no separate analytics
// store, no direct localStorage access (goes through the injected
// repositories only). CosmosAnalyticsRepository (P5) swaps this for
// pre-aggregated server-side views behind the same interface.
export class LocalAnalyticsRepository implements AnalyticsRepository {
  constructor(
    private users: UserRepository,
    private organizations: OrganizationRepository,
    private episodes: EpisodeRepository,
    private progress: ProgressRepository,
    private results: ResultsRepository,
    private xp: XPRepository,
    private badges: BadgeRepository,
    private certificates: CertificateRepository
  ) {}

  private async buildRow(user: AppUser, episodeTotal: number): Promise<LearnerSummaryRow> {
    const [prog, res, xpTotal, badgeAwards, certs] = await Promise.all([
      this.progress.getForUser(user.id),
      this.results.listForUser(user.id),
      this.xp.getTotalForUser(user.id),
      this.badges.listAwardsForUser(user.id),
      this.certificates.listForUser(user.id),
    ])

    const episodesCompleted = prog.filter((p) => p.completed).length
    const passedEpisodeIds = new Set(res.filter((r) => r.passed).map((r) => r.episodeId))
    const quizzesAttempted = res.length
    const averageScorePercent =
      res.length > 0 ? Math.round(res.reduce((sum, r) => sum + r.scorePercent, 0) / res.length) : null

    const timestamps = [
      ...prog.flatMap((p) => [p.startedAt, p.completedAt]),
      ...res.map((r) => r.submittedAt),
      ...badgeAwards.map((b) => b.awardedAt),
      ...certs.map((c) => c.issuedAt),
    ].filter((t): t is string => Boolean(t))
    const sortedTimestamps = timestamps.sort()
    const lastActivityAt = sortedTimestamps.length > 0 ? sortedTimestamps[sortedTimestamps.length - 1] : null

    let status: LearnerStatus
    if (episodesCompleted === 0 && quizzesAttempted === 0) {
      status = 'not_started'
    } else if (episodesCompleted >= episodeTotal && episodeTotal > 0 && passedEpisodeIds.size >= episodeTotal) {
      status = 'completed_all'
    } else if (averageScorePercent !== null && quizzesAttempted > passedEpisodeIds.size) {
      // Has one or more failed/unresolved attempts and hasn't finished the
      // full track yet — flagged so a manager can offer support.
      status = 'needs_attention'
    } else {
      status = 'in_progress'
    }

    return {
      userId: user.id,
      displayName: user.displayName,
      role: user.role,
      avatarColor: user.avatarColor,
      episodesCompleted,
      episodesTotal: episodeTotal,
      quizzesPassed: passedEpisodeIds.size,
      quizzesAttempted,
      averageScorePercent,
      xpTotal,
      badgesEarned: badgeAwards.length,
      certificatesIssued: certs.length,
      lastActivityAt,
      status,
    }
  }

  async listLearnerRows(orgId: string, roleFilter?: UserRole[]): Promise<LearnerSummaryRow[]> {
    const allowedRoles = roleFilter ?? LEARNER_ROLES
    const [orgUsers, episodes] = await Promise.all([this.users.listByOrg(orgId), this.episodes.list()])
    // orgId is enforced by listByOrg itself — this function never touches
    // any user outside the requested organization (Document 11 §5).
    const learners = orgUsers.filter((u) => allowedRoles.includes(u.role))
    return Promise.all(learners.map((u) => this.buildRow(u, episodes.length)))
  }

  async getOrgMetrics(orgId: string): Promise<OrgMetrics> {
    const org = await this.organizations.getById(orgId)
    const rows = await this.listLearnerRows(orgId, LEARNER_ROLES)

    const totalLearners = rows.length
    const activeLearners = rows.filter((r) => r.lastActivityAt !== null).length
    const episodeCompletionRatePercent =
      totalLearners > 0 && rows[0].episodesTotal > 0
        ? Math.round(
            (rows.reduce((sum, r) => sum + r.episodesCompleted, 0) / (totalLearners * rows[0].episodesTotal)) * 100
          )
        : 0
    const learnerCompletionRatePercent =
      totalLearners > 0
        ? Math.round(
            (rows.filter((r) => r.episodesTotal > 0 && r.episodesCompleted >= r.episodesTotal).length /
              totalLearners) *
              100
          )
        : 0

    const rowsWithScores = rows.filter((r) => r.averageScorePercent !== null)
    const averageQuizScorePercent =
      rowsWithScores.length > 0
        ? Math.round(rowsWithScores.reduce((sum, r) => sum + (r.averageScorePercent ?? 0), 0) / rowsWithScores.length)
        : null

    const totalAttempts = rows.reduce((sum, r) => sum + r.quizzesAttempted, 0)
    const totalPassedAttempts = rows.reduce((sum, r) => sum + r.quizzesPassed, 0)
    const quizPassRatePercent = totalAttempts > 0 ? Math.round((totalPassedAttempts / totalAttempts) * 100) : null

    return {
      orgId,
      orgName: org?.name ?? orgId,
      totalLearners,
      activeLearners,
      learnerCompletionRatePercent,
      episodeCompletionRatePercent,
      averageQuizScorePercent,
      quizPassRatePercent,
      totalCyberPoints: rows.reduce((sum, r) => sum + r.xpTotal, 0),
      totalBadgesEarned: rows.reduce((sum, r) => sum + r.badgesEarned, 0),
      certificatesIssued: rows.reduce((sum, r) => sum + r.certificatesIssued, 0),
      learnersNeedingAttention: rows.filter((r) => r.status === 'needs_attention' || r.status === 'not_started')
        .length,
    }
  }

  async getLeaderboard(orgId: string, limit = 10): Promise<LeaderboardEntry[]> {
    const orgUsers = await this.users.listByOrg(orgId)
    const eligible = orgUsers.filter((u) => LEARNER_ROLES.includes(u.role) && !u.leaderboardOptOut)

    const entries = await Promise.all(
      eligible.map(async (u) => ({
        userId: u.id,
        displayName: u.displayName,
        avatarColor: u.avatarColor,
        xpTotal: await this.xp.getTotalForUser(u.id),
        badgesEarned: (await this.badges.listAwardsForUser(u.id)).length,
      }))
    )

    return entries
      .filter((e) => e.xpTotal > 0)
      .sort((a, b) => b.xpTotal - a.xpTotal)
      .slice(0, limit)
      .map((e, i) => ({ ...e, rank: i + 1 }))
  }

  async getBadgeDistribution(orgId: string): Promise<BadgeDistributionEntry[]> {
    const [orgUsers, definitions] = await Promise.all([this.users.listByOrg(orgId), this.badges.listDefinitions()])
    const learners = orgUsers.filter((u) => LEARNER_ROLES.includes(u.role))
    const awardsByUser = await Promise.all(learners.map((u) => this.badges.listAwardsForUser(u.id)))
    const counts = new Map<string, number>()
    for (const awards of awardsByUser) {
      for (const award of awards) {
        counts.set(award.badgeKey, (counts.get(award.badgeKey) ?? 0) + 1)
      }
    }
    return definitions.map((def) => ({
      badgeKey: def.badgeKey,
      name: def.name,
      iconEmoji: def.iconEmoji,
      learnersEarned: counts.get(def.badgeKey) ?? 0,
    }))
  }

  async getEpisodePerformance(orgId: string): Promise<EpisodePerformanceEntry[]> {
    const [orgUsers, episodes] = await Promise.all([this.users.listByOrg(orgId), this.episodes.list()])
    const learners = orgUsers.filter((u) => LEARNER_ROLES.includes(u.role))
    const resultsByUser = await Promise.all(learners.map((u) => this.results.listForUser(u.id)))
    const allResults = resultsByUser.flat()

    return episodes.map((ep) => {
      const forEpisode = allResults.filter((r) => r.episodeId === ep.id)
      const passed = forEpisode.filter((r) => r.passed).length
      return {
        episodeId: ep.id,
        title: ep.title,
        attempts: forEpisode.length,
        passRatePercent: forEpisode.length > 0 ? Math.round((passed / forEpisode.length) * 100) : null,
      }
    })
  }
}
